import { mountWidget } from './mount/mountWidget';
import { mountWithPolling } from './mount/polling';
import { widgetInstances } from './mount/registry';
import type { WidgetInstance, WidgetOptions } from './mount/types';

function resolveContainer(target: string | HTMLElement | undefined): HTMLElement | null {
  if (!target) {
    return null;
  }

  if (target instanceof HTMLElement) {
    return target;
  }

  if (typeof target === 'string') {
    const element = document.querySelector(target);
    return element instanceof HTMLElement ? element : null;
  }

  return null;
}

function unmountWidget(container: HTMLElement | string): void {
  const element = resolveContainer(container);
  if (!element) return;

  const existing = widgetInstances.get(element);
  if (existing) {
    existing.instance.unmount();
  }
}

declare global {
  interface Window {
    CotizadorWidget: {
      mount: (target: string | HTMLElement, options?: WidgetOptions) => Promise<WidgetInstance>;
      mountWhenReady: (selector: string, options?: WidgetOptions) => Promise<WidgetInstance>;
      unmount: (target: string | HTMLElement) => void;
      getInstances: () => HTMLElement[];
    };
  }
}

async function mount(target: string | HTMLElement, options?: WidgetOptions): Promise<WidgetInstance> {
  const container = resolveContainer(target);

  if (!container) {
    // If it is a selector and waitForElement is enabled, poll for the element
    if (typeof target === 'string' && options?.waitForElement) {
      return await mountWithPolling(target, options, options.waitInterval ?? 500, options.waitTimeout ?? 10000);
    }
    throw new Error('Container element not found');
  }

  return await mountWidget(container, options);
}

if (typeof window !== 'undefined') {
  window.CotizadorWidget = {
    mount,
    unmount: unmountWidget,
    getInstances: () => Array.from(widgetInstances.keys()),
    mountWhenReady: (selector: string, options?: WidgetOptions) => {
      return mountWithPolling(selector, options, options?.waitInterval ?? 500, options?.waitTimeout ?? 10000);
    },
  };
}

export default window.CotizadorWidget;
